// Copyright Abridged, Inc. 2021,2024. All Rights Reserved.
// Node module: @collabland/sdk
// This file is licensed under the MIT License.
// License text available at https://opensource.org/license/mit/

import {inject, Provider} from '@loopback/core';
import {getService, RestDataSource} from '../datasources/index.js';
import {IntrospectionRequest} from '../models/introspection-request.model.js';
import {IntrospectionResponse} from '../models/introspection-response.model.js';
import {RevocationRequest} from '../models/revocation-request.model.js';
import {AuthorizationCodeRequest} from '../models/authorization-code-request.model.js';
import {ClientCredentialsRequest} from '../models/client-credentials-request.model.js';
import {PasswordRequest} from '../models/password-request.model.js';
import {RefreshTokenRequest} from '../models/refresh-token-request.model.js';
import {AccessTokenResponse} from '../models/access-token-response.model.js';

/**
 * The service interface is generated from OpenAPI spec with operations tagged
 * by OAuth2Controller.
 */
export interface OAuth2Service {
  /**
   */
  getOpenIdConfig(): Promise<unknown>;

  /**
   * @param response_type
   * @param client_id
   * @param redirect_uri
   * @param scope
   * @param state
   * @param code_challenge
   * @param code_challenge_method
   * @param platform
   * @param login_provider
   */
  authorize(
    response_type: 'code' | 'token' | 'id_token',
    client_id: string,
    redirect_uri?: string,
    scope?: string,
    state?: string,
    code_challenge?: string,
    code_challenge_method?: string,
    platform?: string,
    login_provider?: string,
  ): Promise<unknown>;

  /**
   * @param slug
   * @param aeToken
   * @param userAuthorization User decision on the authorization request
   */
  callback(
    slug: string,
    aeToken: string,
    userAuthorization: 'granted' | 'denied' | undefined,
  ): Promise<unknown>;

  /**
   * @param _requestBody Access Token Request
   * @returns OK
   */
  introspect(
    _requestBody: IntrospectionRequest,
  ): Promise<IntrospectionResponse>;

  /**
   * @param _requestBody Revoke Token Request
   */
  revoke(_requestBody: RevocationRequest): Promise<unknown>;

  /**
   * @param _requestBody Access Token Request
   * @returns OK
   */
  token(
    _requestBody:
      | AuthorizationCodeRequest
      | ClientCredentialsRequest
      | PasswordRequest
      | RefreshTokenRequest,
  ): Promise<AccessTokenResponse>;
}

export class OAuth2ServiceProvider implements Provider<OAuth2Service> {
  constructor(
    // rest must match the name property in the datasource json file
    @inject('datasources.rest')
    protected dataSource: RestDataSource = new RestDataSource(),
  ) {}

  async value(): Promise<OAuth2Service> {
    const service = await getService<{apis: {OAuth2Controller: OAuth2Service}}>(
      this.dataSource,
    );
    return service.apis['OAuth2Controller'];
  }
}
